import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { canManage } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, ArrowRight, Check, X, MinusCircle, HelpCircle, FileDown, FileCheck2, Trash2, ListChecks } from "lucide-react";

const STATUSES = [
  { key: "compliant", label: "Compliant", icon: Check, cls: "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-50", activeCls: "bg-emerald-600 text-white border-emerald-700" },
  { key: "partial", label: "Partial", icon: MinusCircle, cls: "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-50", activeCls: "bg-amber-500 text-white border-amber-600" },
  { key: "non_compliant", label: "Non-compliant", icon: X, cls: "bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-50", activeCls: "bg-rose-600 text-white border-rose-700" },
  { key: "na", label: "N/A", icon: HelpCircle, cls: "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-50", activeCls: "bg-slate-700 text-white border-slate-800" },
];

function ItemRow({ item, onSave }) {
  const [status, setStatus] = useState(item.status || "");
  const [notes, setNotes] = useState(item.notes || "");
  const [verifiedBy, setVerifiedBy] = useState(item.verified_by || "");
  const [createAction, setCreateAction] = useState(item.create_action || false);
  const [saving, setSaving] = useState(false);

  const setStatusInstant = async (s) => {
    setStatus(s);
    // Optimistic default: non-compliant/partial → auto-tick create_action
    const nextCreate = s === "non_compliant" || s === "partial" ? true : s === "compliant" || s === "na" ? false : createAction;
    setCreateAction(nextCreate);
    setSaving(true);
    await onSave({ status: s, notes, verified_by: verifiedBy, create_action: nextCreate });
    setSaving(false);
  };

  const saveOnBlur = async () => {
    setSaving(true);
    await onSave({ status: status || null, notes, verified_by: verifiedBy, create_action: createAction });
    setSaving(false);
  };

  const toggleCreateAction = async () => {
    const next = !createAction;
    setCreateAction(next);
    setSaving(true);
    await onSave({ status: status || null, notes, verified_by: verifiedBy, create_action: next });
    setSaving(false);
  };

  return (
    <div className="border-b border-slate-100 p-4" data-testid={`comp-item-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900">{item.title}</div>
          <div className="mt-1 flex gap-3 text-xs text-slate-500 flex-wrap">
            <span>Primary: <b className="text-slate-800">{item.primary_owner || "—"}</b></span>
            {item.secondary_owner && <span>Secondary: <b className="text-slate-800">{item.secondary_owner}</b></span>}
            <Badge variant="outline" className="text-[10px] border-slate-300 h-5">{item.evidence_type}</Badge>
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-1">
        {STATUSES.map((s) => {
          const Icon = s.icon;
          const active = status === s.key;
          return (
            <button data-testid={`comp-status-${item.id}-${s.key}`} key={s.key} type="button" onClick={() => setStatusInstant(s.key)}
              className={`text-[11px] font-semibold rounded-md px-2 py-1.5 border transition-colors inline-flex items-center justify-center gap-1 ${active ? s.activeCls : `bg-white ${s.cls}`}`}>
              <Icon className="h-3.5 w-3.5" />{s.label}
            </button>
          );
        })}
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Textarea data-testid={`comp-notes-${item.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveOnBlur} rows={2} placeholder="Findings, evidence seen or gap identified…" className="text-sm" />
        <Input data-testid={`comp-verified-${item.id}`} value={verifiedBy} onChange={(e) => setVerifiedBy(e.target.value)} onBlur={saveOnBlur} placeholder="Verified by (name)…" className="text-sm" />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button data-testid={`comp-toggle-qip-${item.id}`} type="button" onClick={toggleCreateAction}
          className={`text-[11px] rounded-md px-2 py-1 border transition-colors inline-flex items-center gap-1 ${createAction ? "bg-indigo-50 border-indigo-300 text-indigo-800" : "bg-white border-slate-200 text-slate-500"}`}>
          {createAction ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
          {createAction ? "Will become a QIP action" : "Add to QIP on completion"}
        </button>
        {saving && <span className="text-[10px] text-slate-400">Saving…</span>}
      </div>
    </div>
  );
}

export default function Compliance() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { review_id } = useParams();
  const [list, setList] = useState([]);
  const [review, setReview] = useState(null);
  const [stages, setStages] = useState({});
  const [currentStage, setCurrentStage] = useState("stage_1");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ learner_ref: "", cohort: "", reviewer_name: user?.name || "", notes: "" });
  const [busy, setBusy] = useState(false);

  const loadList = async () => {
    const [rs, tpl] = await Promise.all([api.get("/compliance-reviews"), api.get("/compliance-template")]);
    setList(rs.data); setStages(tpl.data);
  };
  const loadOne = async () => {
    if (!review_id) return;
    const [r, tpl] = await Promise.all([api.get(`/compliance-reviews/${review_id}`), api.get("/compliance-template")]);
    setReview(r.data); setStages(tpl.data);
    const keys = Object.keys(tpl.data);
    if (keys.length && !keys.includes(currentStage)) setCurrentStage(keys[0]);
  };
  useEffect(() => { loadList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { loadOne(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [review_id]);

  const createReview = async () => {
    try { const { data } = await api.post("/compliance-reviews", form);
      toast.success("Review started"); setOpen(false); loadList(); nav(`/compliance/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveItem = async (item_id, patch) => {
    try {
      const { data } = await api.patch(`/compliance-reviews/${review_id}/items/${item_id}`, patch);
      setReview((prev) => ({ ...prev, items: prev.items.map((it) => it.id === item_id ? { ...it, ...data } : it) }));
    } catch { toast.error("Save failed"); }
  };

  const complete = async () => {
    const willTransfer = (review?.items || []).filter((it) => it.create_action).length;
    if (!confirm(`Complete this review?\n\n${willTransfer} item(s) flagged will become RAG-red QIP actions under Leadership & Governance.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/compliance-reviews/${review_id}/complete`, { transfer_to_qip: true });
      toast.success(`Complete · ${data.actions_created} QIP actions created`); loadOne();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const downloadPdf = () => {
    const t = localStorage.getItem("qip_token");
    window.open(`${api.defaults.baseURL}/compliance-reviews/${review_id}/pdf?auth=${t}`, "_blank");
  };

  const deleteReview = async (r) => {
    if (!confirm(`Delete compliance review for "${r.learner_ref}"?`)) return;
    const purge = r.actions_created > 0 && confirm(`Also delete the ${r.actions_created} QIP actions this review created?`);
    try { await api.delete(`/compliance-reviews/${r.id}${purge ? "?purge_generated_actions=true" : ""}`);
      toast.success("Deleted"); loadList(); }
    catch (e) { toast.error("Failed"); }
  };

  // ------ List mode ------
  if (!review_id) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800">Compliance</div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Learner journey evidence pack</h1>
            <p className="text-slate-600 mt-1">Verify every stage of the learner journey against the Evidence Pack Responsibilities Framework. Non-compliant findings flow into your QIP.</p>
          </div>
          {canManage(user?.role) && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="new-compliance-btn" className="bg-indigo-900 hover:bg-indigo-950 text-white"><Plus className="h-4 w-4 mr-1.5" />New compliance review</Button></DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle>Start a compliance review</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Learner name / ID</Label><Input data-testid="comp-learner-ref" value={form.learner_ref} onChange={(e) => setForm({ ...form, learner_ref: e.target.value })} placeholder="e.g. J.Bloggs / ULN 1234567890" /></div>
                  <div><Label>Cohort / programme (optional)</Label><Input value={form.cohort} onChange={(e) => setForm({ ...form, cohort: e.target.value })} placeholder="e.g. WMCA Autumn Skills Bootcamp" /></div>
                  <div><Label>Reviewer</Label><Input value={form.reviewer_name} onChange={(e) => setForm({ ...form, reviewer_name: e.target.value })} /></div>
                  <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
                </div>
                <DialogFooter><Button data-testid="create-compliance-btn" onClick={createReview} disabled={!form.learner_ref} className="bg-indigo-900 hover:bg-indigo-950 text-white">Start review</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.length === 0 && <Card className="p-8 text-center text-slate-500 md:col-span-2 xl:col-span-3">No compliance reviews yet.</Card>}
          {list.map((r) => {
            const done = r.items.filter((i) => i.status).length;
            const total = r.items.length;
            const nc = r.items.filter((i) => i.status === "non_compliant").length;
            return (
              <Card key={r.id} className="p-5 border-slate-200 hover:shadow-md transition-shadow duration-200" data-testid={`comp-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{new Date(r.created_at).toLocaleDateString("en-GB")}</div>
                    <div className="font-bold text-slate-900 mt-1 truncate" style={{ fontFamily: "Plus Jakarta Sans" }}>{r.learner_ref}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.cohort || "—"} · Reviewer {r.reviewer_name}</div>
                  </div>
                  {r.status === "complete" ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><Check className="h-3 w-3 mr-1" />Complete</Badge> : <Badge variant="outline" className="border-amber-300 text-amber-800">In progress</Badge>}
                </div>
                <div className="mt-3 text-xs text-slate-500">{done} / {total} items reviewed · {nc} non-compliant</div>
                <Progress value={(done / Math.max(total, 1)) * 100} className="h-2 mt-1" />
                {r.actions_created > 0 && <div className="mt-2 text-xs text-emerald-800">→ {r.actions_created} QIP actions created</div>}
                <div className="mt-3 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => nav(`/compliance/${r.id}`)} data-testid={`open-comp-${r.id}`}>Open <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
                  {canManage(user?.role) && <Button variant="ghost" size="sm" onClick={() => deleteReview(r)} className="ml-auto text-slate-500 hover:text-rose-700" data-testid={`del-comp-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // ------ Detail mode ------
  if (!review) return <div className="text-slate-500">Loading review…</div>;
  const stageKeys = Object.keys(stages);
  const stageItems = review.items.filter((i) => i.stage_key === currentStage);
  const overallDone = review.items.filter((i) => i.status).length;
  const overallTotal = review.items.length;
  const flagged = review.items.filter((i) => i.create_action).length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-indigo-800">Compliance · {review.learner_ref}</div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>Learner journey compliance review</h1>
          <p className="text-slate-600 mt-1 text-sm">Reviewer: {review.reviewer_name} · Started {new Date(review.created_at).toLocaleDateString("en-GB")} · {overallDone}/{overallTotal} reviewed · <b className="text-indigo-800">{flagged}</b> flagged for QIP</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => nav("/compliance")}>Back</Button>
          <Button variant="outline" onClick={downloadPdf} data-testid="comp-pdf-btn"><FileDown className="h-4 w-4 mr-1.5" />PDF</Button>
          {review.status !== "complete" && canManage(user?.role) && (
            <Button data-testid="complete-comp-btn" onClick={complete} disabled={busy || overallDone === 0} className="bg-emerald-700 hover:bg-emerald-800 text-white"><FileCheck2 className="h-4 w-4 mr-1.5" />{busy ? "Transferring…" : `Complete & transfer ${flagged} to QIP`}</Button>
          )}
        </div>
      </div>

      <Card className="p-4 border-slate-200"><Progress value={(overallDone / Math.max(overallTotal, 1)) * 100} className="h-2" /></Card>

      <Tabs value={currentStage} onValueChange={setCurrentStage}>
        <TabsList className="bg-white border border-slate-200 h-auto flex-wrap p-1">
          {stageKeys.map((k) => {
            const cnt = review.items.filter((i) => i.stage_key === k);
            const d = cnt.filter((i) => i.status).length;
            return (
              <TabsTrigger key={k} value={k} data-testid={`comp-tab-${k}`} className="text-xs data-[state=active]:bg-indigo-900 data-[state=active]:text-white">
                <span className="font-mono text-[10px] opacity-70 mr-1">{stages[k]?.code}</span>{stages[k]?.title?.split("—")[0]?.trim() || k} <span className="ml-1 font-mono text-[10px] opacity-70">{d}/{cnt.length}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <Card className="border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="text-sm text-slate-600">{stages[currentStage]?.description}</div>
        </div>
        {stageItems.map((it) => (<ItemRow key={it.id} item={it} onSave={(patch) => saveItem(it.id, patch)} />))}
      </Card>

      {review.status === "complete" && (
        <Card className="p-5 border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-2 text-emerald-900 font-semibold"><ListChecks className="h-5 w-5" />Review complete</div>
          <div className="text-sm text-emerald-800 mt-2">{review.actions_created} QIP action(s) created under Leadership & Governance.</div>
        </Card>
      )}
    </div>
  );
}
